import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  EventEmitter,
  Output,
  ElementRef,
  ViewChild,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  PipeTransform,
  Pipe,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { StorageService } from '../../services/storage/storage.service';
import { QuickMenuService } from '../side-nav/quick-menu/quick-menu.service';
import { DashboardService } from '../../pages/dashboard/dashboard.service';

// Participant Record is the one legacy staff page that shows the participation notice.
const PARTICIPANT_RECORD_MENU_ITEM = 25;

@Pipe({
  name: 'safe',
  standalone: false,
})
export class SafePipe implements PipeTransform {
  constructor(private _sanitizer: DomSanitizer) {}
  transform(url) {
    return this._sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
/** Wrapper ifrome component that emits the staff page load event */
@Component({
  selector: 'ag-iframe',
  templateUrl: './iframe.component.html',
  styleUrls: ['./iframe.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IFrameComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('iframe') iframe: ElementRef;
  @Input() url: any;
  @Input() printStatus = false;
  @Input() height: any = '100%';
  @Input() width: any = '100%';
  @Input() menuItem: number;
  @Output() onloaded = new EventEmitter<boolean>();

  iOS: boolean;
  private _sanitizer = inject(DomSanitizer);
  private _storageService = inject(StorageService);
  private _quickMenuService = inject(QuickMenuService);
  private _analyticsService = inject(AnalyticsService);
  private _dashboardService = inject(DashboardService);

  constructor() {
    //
    // detect if its ios platform or not
    // https://stackoverflow.com/questions/9038625/detect-if-device-is-ios
    //
    this.iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.printStatus?.currentValue) {
      this.print();
    }
  }

  ngOnInit() {
    window.addEventListener('message', this._onIframeMessage);
  }

  ngAfterViewInit(): void {
    // 61995 - Circ PS&C - system should shade the PS&C tab that staff is in to make it easily identifiable
    if (this.url && this.url.includes('/ILSP/Circulation/PatronStatusCheckout')) {
      this.postMessageToIframe('send styles to iframe');
    }

    this.iframe.nativeElement.contentWindow.focus();
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this._onIframeMessage);
  }

  private _onIframeMessage = (event) => {
    if (this._quickMenuService.hotkey(event)) {
      return;
    }

    const userData = this._storageService.getUserData();
    const cid = userData.customerId;
    const lid = userData.libraryId;
    const eventData = event.data;

    switch (eventData.Category) {
      case 'Open Lending Policy':
        if (eventData.Event === 'Lending') {
          window.location.href = '/dashboard/lending?cid=' + cid + '&lid=' + lid + '&lending=y';
        }
        break;
      case 'Open Borrowing Policy':
        if (eventData.Event === 'Borrowing') {
          window.location.href = '/dashboard/borrow?cid=' + cid + '&lid=' + lid + '&borrow=y';
        }
        break;
      case 'Lookedup Patron': {
        const { patron_lookup_value1, patron_lookup_value2 } = eventData.Event;
        const patrons = [
          { key: '_curr_pat', value: patron_lookup_value1 },
          { key: '_prev_pat', value: patron_lookup_value2 },
        ];

        for (const { key, value } of patrons) {
          if (value && value.trim() !== '') {
            this._storageService.setItem(key, value);
          }
        }

        break;
      }
      case 'HomeButtonClick': {
        const frame = document.getElementById('iframe-child') as HTMLIFrameElement | null;
        frame?.contentWindow?.location.reload();
        break;
      }
      case 'LenderLists':
        window.location.href = `/staff/ill/lender-lists?cid=${cid}&lid=${lid}`;
        break;
      case 'Configure Custom Fields':
        window.location.href = `/staff/ill/participant-record/custom-fields?cid=${cid}&lid=${lid}&staffUrl=${encodeURIComponent(this.url)}`;
        break;
      case 'Upload ZMap': {
        const xdbProfileKey = eventData.Event.upload_zmap_xdbprofilekey;
        const returnStaffUrl = eventData.Event.upload_zmap_returnstaffurl;
        window.location.href = `/staff/pacadmin/publicsearchresources/z3950-mapping-import/${xdbProfileKey}?staffUrl=${returnStaffUrl}`;
        break;
      }
      case 'close':
        this._dashboardService.closeStaff();
        break;
      case 'Request Again': {
        const requestId = eventData.Event.request_again_id;
        window.location.href = `/request?requestId=${requestId}`;
      }
    }

    if (eventData.Event !== 'IframeAction' && eventData.Event !== 'CloseIframe') {
      this._analyticsService.logEvent(eventData.Category, { page_title: eventData.Event });
    }
  };

  // 61995 - Circ PS&C - system should shade the PS&C tab that staff is in to make it easily identifiable
  // https://embed.plnkr.co/plunk/TRfU7i
  // https://embed.plnkr.co/plunk/WFcL58
  // https://gist.github.com/pbojinov/8965299 (used this one for implemetation)
  postMessageToIframe(message) {
    const targetWindow =
      this.iframe.nativeElement.contentWindow || this.iframe.nativeElement.contentDocument;
    this.send(targetWindow, message);
  }

  send(targetWindow, message) {
    switch (message) {
      case 'send styles to iframe':
        const message = this._createStylesEvent();
        setTimeout(() => {
          this._sendStylesToIframe(targetWindow, message);
        }, 3000);
        break;
      default:
        break;
    }
  }

  private _sendStylesToIframe(targetWindow, message) {
    let isDone = false;
    do {
      if (targetWindow) {
        targetWindow.postMessage(message, this.url);
        isDone = true;
      }
    } while (!targetWindow && !isDone);
  }

  private _createStylesEvent() {
    const headerStyles = this._storageService.getItem('layoutSettings');
    const object = {
      event: 'styles',
      headerStyles: headerStyles,
    };
    return object;
  }

  refresh() {
    this.iframe.nativeElement.contentWindow.reload();
  }

  print() {
    window.print();
  }

  //
  // santitize iframe
  //
  trustSrcUrl(data) {
    return this._sanitizer.bypassSecurityTrustResourceUrl(data);
  }

  iframeOnLoad() {
    this.url ? this.onloaded.emit(true) : this.onloaded.emit(false);
  }

  get isParticipantRecord(): boolean {
    return this.menuItem === PARTICIPANT_RECORD_MENU_ITEM;
  }
}
